import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Subscription} from 'rxjs';
import {ReferralService} from '../../../shared/services/referral.service';
import {ReferralApiService} from '../../../shared/services/referral-api.service';
import {RefPromptService} from '../shared/ref-prompt.component';
import {PER_PAGE} from '../shared/ref-utils';

type PartnerLevel = 'Gold' | 'Silver' | 'Bronze';
type KycStatus = 'NOT_SUBMITTED' | 'SUBMITTED' | 'UNDER_REVIEW' | 'VERIFIED' | 'REJECTED' | 'RESUBMISSION_REQUIRED';
type PartnerStatus = 'ACTIVE' | 'INACTIVE' | 'BLOCKED';

interface PartnerRow {
    id: number;
    name: string;
    mobile: string;
    code: string;
    status: PartnerStatus;
    kycStatus: KycStatus;
    refs: number;
    enr: number;
    earned: number;
    level: PartnerLevel;
    registeredAt: string;
}

interface ReferrerDetail {
    wallet: { available_points: number };
    referrals_by_status: { [status: string]: number };
}

export const KYC_LABEL: { [k in KycStatus]: string } = {
    NOT_SUBMITTED: 'Not submitted',
    SUBMITTED: 'Submitted',
    UNDER_REVIEW: 'Under review',
    VERIFIED: 'Verified',
    REJECTED: 'Rejected',
    RESUBMISSION_REQUIRED: 'Resubmission required'
};

@Component({
    selector: 'app-referral-partners',
    templateUrl: './referral-partners.component.html'
})
export class ReferralPartnersComponent implements OnInit, OnDestroy {
    q = '';
    kyc = '';
    status = '';
    page = 0;
    per = PER_PAGE;
    total = 0;
    loading = false;
    list: PartnerRow[] = [];

    selected: PartnerRow;
    selDetail: ReferrerDetail = null;
    blocking = false;

    KYC_LABEL = KYC_LABEL;
    private subs: Subscription[] = [];

    constructor(public referralService: ReferralService,
                private referralApi: ReferralApiService,
                private route: ActivatedRoute,
                private prompt: RefPromptService) {
    }

    ngOnInit() {
        this.subs.push(this.route.queryParamMap.subscribe(qp => {
            if (qp.has('q')) {
                this.q = qp.get('q');
            }
            this.load();
            if (qp.has('open')) {
                this.open(+qp.get('open'));
            }
        }));
    }

    ngOnDestroy() {
        this.subs.forEach(s => s.unsubscribe());
    }

    get pageRows(): PartnerRow[] {
        return this.list;
    }

    onFilter() {
        this.page = 0;
        this.load();
    }

    onPageChange(page: number) {
        this.page = page;
        this.load();
    }

    load() {
        this.loading = true;
        const params: any = {page: this.page + 1, per_page: this.per};
        const q = this.q.trim();
        if (q) {
            // One box covers three backend filters that can't all be sent at once (the API ANDs
            // whichever of name/mobile/referral_code are present) — route to the one that fits.
            if (/^[+\d][\d\s-]*$/.test(q)) {
                params.mobile = q;
            } else if (!/\s/.test(q) && /[A-Za-z]/.test(q) && /\d/.test(q)) {
                // Referral codes are alphanumeric with no spaces (e.g. EMR7JG4ZY); matched exactly.
                params.referral_code = q;
            } else {
                params.name = q;
            }
        }
        // Backend KYC statuses are uppercase, and some filter buckets (e.g. "Pending") map to more
        // than one real status — the backend accepts a comma-separated list for kyc_status.
        if (this.kyc) {
            params.kyc_status = this.kyc.toUpperCase();
        }
        if (this.status) {
            params.status = this.status.toUpperCase();
        }

        this.referralApi.getReferrers(params).subscribe({
            next: (data: any) => {
                this.total = data?.total ?? 0;
                this.list = (data?.data ?? []).map((r: any) => this.toRow(r));
                this.loading = false;
            },
            error: () => {
                this.list = [];
                this.total = 0;
                this.loading = false;
            }
        });
    }

    private toRow(r: any): PartnerRow {
        const enr = r.converted_count ?? 0;
        return {
            id: r.id,
            name: r.name,
            mobile: r.mobile,
            code: r.referral_code,
            status: r.status,
            kycStatus: r.kyc_status,
            refs: r.referrals_count ?? 0,
            enr,
            earned: r.lifetime_earned_points ?? r.available_points ?? 0,
            // Same thresholds the admin app already used before this was wired to the backend.
            level: enr >= 10 ? 'Gold' : enr >= 4 ? 'Silver' : 'Bronze',
            registeredAt: r.registered_at
        };
    }

    fmt(iso: string): string {
        return iso ? new Date(iso).toLocaleDateString('en-IN', {day: 'numeric', month: 'short', year: 'numeric'}) : '—';
    }

    // Backend values are uppercase; the pill styling in referral-shell.component.scss keys off
    // lowercase tokens (.p-verified, .p-none, .p-under_review, .p-rejected, ...).
    kycClass(k: KycStatus): string {
        if (k === 'NOT_SUBMITTED') {
            return 'none';
        }
        if (k === 'SUBMITTED') {
            return 'pending';
        }
        if (k === 'RESUBMISSION_REQUIRED') {
            return 'rejected';
        }
        return k.toLowerCase();
    }

    statusClass(s: PartnerStatus): string {
        return s === 'ACTIVE' ? 'active' : s === 'BLOCKED' ? 'blocked' : 'none';
    }

    statusLabel(s: PartnerStatus): string {
        return s === 'ACTIVE' ? 'Active' : s === 'BLOCKED' ? 'Blocked' : 'Inactive';
    }

    open(id: number) {
        const row = this.list.find(x => x.id === id);
        if (!row) {
            return;
        }
        this.selected = row;
        this.selDetail = null;
        this.referralApi.getReferrer(id).subscribe((data: any) => {
            this.selDetail = data;
        });
    }

    close() {
        this.selected = null;
        this.selDetail = null;
    }

    async toggleBlock() {
        const p = this.selected;
        if (p.status !== 'BLOCKED') {
            const reason = await this.prompt.ask({
                title: `Block ${p.name}?`,
                text: 'They won’t be able to log in or add referrals. Existing referrals and payouts stay as they are.',
                label: 'Reason (shown to the partner)',
                okText: 'Block partner'
            });
            if (reason !== null) {
                this.updateStatus('BLOCKED', reason || 'Blocked by admin');
            }
        } else {
            this.updateStatus('ACTIVE', 'Unblocked by admin');
        }
    }

    private updateStatus(status: PartnerStatus, reason: string) {
        const p = this.selected;
        this.blocking = true;
        this.referralApi.updateReferrerStatus(p.id, {status, reason}).subscribe({
            next: () => {
                p.status = status;
                this.blocking = false;
                this.referralService.toast(status === 'BLOCKED' ? 'Partner blocked' : 'Partner unblocked');
            },
            error: () => {
                this.blocking = false;
            }
        });
    }

    exportCsv() {
        this.referralService.csv('referral-partners.csv', ([['Name', 'Phone', 'Code', 'Referrals', 'Enrolled', 'Earned', 'KYC', 'Status']] as any[][]).concat(
            this.list.map(p => [p.name, p.mobile, p.code, p.refs, p.enr, p.earned, KYC_LABEL[p.kycStatus], p.status])));
    }
}
